import { Component, inject, signal, ViewChild, OnInit } from '@angular/core';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { Pagination, PictureInPictureComponent } from '@peca/design-system';
import { InboundInvoice } from '../../../models/fiscal/inbound-invoice.model';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order.model';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { PurchaseOrderService } from '../../../services/purchase.service';
import { InboundInvoicesService } from '../../../services/inbound-invoices.service';
import { PurchaseOrderQuery } from '../../../models/purchase-order/purchase-order-query.model';
import { InboundInvoiceQuery } from '../../../models/fiscal/inbound-invoice-query.model';
import { FiscalPanelModalRemoveOrderComponent } from '../fiscal-panel-modal-remove-order/fiscal-panel-modal-remove-order.component';
import { DocumentPreviewComponent, PersistQueryService, ToastService } from '@peca/commons';
import { FiscalPanelModalSendOmieComponent } from '../fiscal-panel-modal-send-omie/fiscal-panel-modal-send-omie.component';
import { ConfirmableActionService } from '@peca/backoffice/services/confirmable-action.service';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { PurchaseOrderDetailsComponent } from '../../purchase-order/purchase-order-details/purchase-order-details.component';
import getRelativeDate from '../../../utils/get-relative-date.utils';
import { KeycloakCredentialsService } from '@peca/keycloak';
import { ErrorResponse } from 'libs/commons/src/lib/interceptors/error-response.model';

@Component({
  templateUrl: './fiscal-panel-dashboard.component.html',
  styleUrls: ['./fiscal-panel-dashboard.component.scss'],
  standalone: false,
})
export class FiscalDashboardComponent implements OnInit {
  @ViewChild('modalRemoveOrder') modalRemoveOrder!: FiscalPanelModalRemoveOrderComponent;
  @ViewChild('modalSendOmie') modalSendOmie!: FiscalPanelModalSendOmieComponent;
  @ViewChild('pipPurchaseOrder') pipPurchaseOrder!: PurchaseOrderDetailsComponent;
  @ViewChild('pip') pip!: PictureInPictureComponent;
  @ViewChild('documentPreview') documentPreview!: DocumentPreviewComponent;

  private innitialPagination = { page: 1, size: 12, total: 0, items: 0, sort: 'audit.createdAt,desc' };
  private purchaseService = inject(PurchaseOrderService);
  private inboundService = inject(InboundInvoicesService);
  private toast = inject(ToastService);
  private confirmAction = inject(ConfirmableActionService);
  private persistQueryService = inject(PersistQueryService);
  private credentials = inject(KeycloakCredentialsService);

  breadcrumb = [{ label: 'Compras', path: '/compras' }, { label: 'Painel Fiscal' }];
  query = signal<PurchaseOrderQuery & InboundInvoiceQuery>({});
  selectedOrderId = signal('');

  pendingOrders = signal<AsyncState<PurchaseOrder[]>>({});
  pendingOrdersPagination = signal<Pagination>(this.innitialPagination);

  invoiceSeller = signal<AsyncState<InboundInvoice[]>>({});
  invoiceSellerPagination = signal<Pagination>(this.innitialPagination);

  invoiceReconciled = signal<AsyncState<PurchaseOrder[]>>({});
  invoiceReconciledPagination = signal<Pagination>(this.innitialPagination);

  get pendingOrdersQuery() {
    const { page, size, sort } = this.pendingOrdersPagination();
    const { commerceReference, branchId, amount, startDate, endDate } = this.query();
    return {
      page,
      size,
      sort,
      reconciled: false,
      fiscalArchived: false,
      commerceReference,
      branchId,
      amount,
      startDate: startDate || getRelativeDate(-30),
      endDate,
      processed: true,
      excludeSellerCnpj: '47698109000370',
      excludeStatus: ['CANCELED'],
    } as PurchaseOrderQuery;
  }

  get invoiceSellerQuery() {
    const { page, size, sort } = this.invoiceSellerPagination();
    const { commerceReference, branchId, amount, endDate, startDate, inboundInvoiceCode } = this.query();

    return {
      page,
      size,
      sort,
      branchId,
      commerceReference,
      amount,
      endDate,
      startDate: startDate || getRelativeDate(-30),
      inboundInvoiceCode,
      fiscalArchived: false,
      reconciled: false,
    } as InboundInvoiceQuery;
  }

  get invoiceReconciledQuery() {
    const { page, size, sort } = this.invoiceReconciledPagination();
    const { commerceReference, branchId, amount, startDate, endDate } = this.query();
    return {
      ...this.query(),
      page,
      size,
      sort,
      reconciled: true,
      commerceReference,
      branchId,
      amount,
      startDate: startDate || getRelativeDate(-30),
      endDate,
      fiscalArchived: false,
      processed: true,
      excludeSellerCnpj: '47698109000370',
      excludeStatus: ['CANCELED'],
    } as PurchaseOrderQuery;
  }

  ngOnInit() {
    const restoredQuery = this.persistQueryService.read<PurchaseOrderQuery>('peca-purchase-fiscal-panel-list');
    this.query.set(restoredQuery || {});
    this.persistQueryService.rewriteQueryString('peca-purchase-fiscal-panel-list', restoredQuery);

    this.fetchPendingOrders(this.pendingOrdersQuery);
    this.fetchInvoiceSeller(this.invoiceSellerQuery);
    this.fetchInvoiceReconciledOrders(this.invoiceReconciledQuery);
  }

  onSearch(query: PurchaseOrderQuery) {
    this.query.set(query);
    const newPagination = { ...this.innitialPagination, page: 1 };
    this.pendingOrdersPagination.set(newPagination);
    this.invoiceSellerPagination.set(newPagination);
    this.invoiceReconciledPagination.set(newPagination);

    this.fetchPendingOrders(this.pendingOrdersQuery);
    this.fetchInvoiceReconciledOrders(this.invoiceReconciledQuery);
    this.fetchInvoiceSeller(this.invoiceSellerQuery);
  }

  fetchPendingOrders(query: PurchaseOrderQuery) {
    this.pendingOrders.set({ loading: true });

    this.purchaseService.fetchOrders(query).subscribe({
      next: (res) => {
        const { items } = res;
        this.pendingOrders.set({ data: items, loading: false });
        this.pendingOrdersPagination.update((value) => ({ ...value, ...this.pageableToPagination(res) }));
      },
      error: () => {
        this.pendingOrders.set({ failure: true });
      },
    });
  }

  fetchInvoiceSeller(query: InboundInvoiceQuery) {
    this.invoiceSeller.set({ loading: true });

    this.inboundService.fetchInboundInvoices(query).subscribe({
      next: (res) => {
        const { items } = res;
        this.invoiceSeller.set({ data: items, loading: false });
        this.invoiceSellerPagination.update((value) => ({ ...value, ...this.pageableToPagination(res) }));
      },
      error: () => {
        this.invoiceSeller.set({ failure: true });
      },
    });
  }

  fetchInvoiceReconciledOrders(query: PurchaseOrderQuery) {
    this.invoiceReconciled.set({ loading: true });

    this.purchaseService.fetchOrders(query).subscribe({
      next: (res) => {
        const { items } = res;
        this.invoiceReconciled.set({ data: items, loading: false });
        this.invoiceReconciledPagination.update((value) => ({ ...value, ...this.pageableToPagination(res) }));
      },
      error: () => {
        this.invoiceReconciled.set({ failure: true });
      },
    });
  }

  pageableToPagination(pageable: Pageable<any>): Pagination {
    return {
      page: pageable.currentPage,
      size: pageable.pageSize,
      total: pageable.totalPages,
      items: pageable.totalItems,
    };
  }

  openPictureInPicture(orderId: string) {
    if (this.pip.active()) return;
    this.selectedOrderId.set(orderId);
    this.pip.open();
  }

  onClickRemovePendingOrders(id: string, commerceReference: string) {
    this.modalRemoveOrder.open(id, commerceReference);
  }

  removePendingOrder({ id, justification }: { id: string; justification: string }) {
    this.purchaseService.archiveOrder(id, justification, true).subscribe({
      next: () => {
        const newPagination = { ...this.innitialPagination, page: 1 };
        this.pendingOrdersPagination.set(newPagination);
        this.fetchPendingOrders(this.pendingOrdersQuery);
      },
      error: (err: any) => {
        if (err && err.status === 409) {
          this.toast.warning('Pedido já conciliado, não é possível removê-lo');
          return;
        }
        this.toast.failure('Erro ao remover pedido pendente, tente novamente mais tarde');
      },
    });
  }

  onClickSendOmie(orderId: string) {
    this.modalSendOmie.open(orderId);
  }

  sendToOmie(orderId: string, nfCode: string) {
    this.purchaseService.sendErp(orderId, nfCode).subscribe({
      next: () => {
        const newPagination = { ...this.innitialPagination, page: 1 };
        this.pendingOrdersPagination.set(newPagination);
        this.invoiceSellerPagination.set(newPagination);
        this.invoiceReconciledPagination.set(newPagination);

        this.fetchPendingOrders(this.pendingOrdersQuery);
        this.fetchInvoiceReconciledOrders(this.invoiceReconciledQuery);
        this.fetchInvoiceSeller(this.invoiceSellerQuery);
      },
      error: (err: any) => {
        if (err && err.status === 409) {
          this.toast.warning('Pedido já enviado para o Omie, não é possível reenviá-lo');
          return;
        } else if (err && err.status === 404) {
          this.toast.warning('Este pedido foi removido do painel fiscal, não sendo possível associá-lo.');
          return;
        }
        this.toast.failure('Erro ao enviar pedido para o Omie, tente novamente mais tarde');
      },
    });
  }

  private handleNFeError(): void {
    this.toast.failure('Erro ao remover NF-e, tente novamente mais tarde');
  }

  onClickRemoveNf(id: string, code: string) {
    this.confirmAction.confirm(`Você está removendo a <b>NF-e ${code}</b> da listagem atual, mas ela continuará disponível no Omie.`, null, true).subscribe({
      next: ({ confirmed }) => {
        if (!confirmed) return;

        this.inboundService.archiveInboundInvoice(id, true).subscribe({
          next: () => {
            const newPagination = { ...this.innitialPagination, page: 1 };
            this.invoiceSellerPagination.set(newPagination);

            this.fetchInvoiceSeller(this.invoiceSellerQuery);
          },
          error: (res) => {
            if (res.status === 409) {
              this.toast.warning('NF-e já conciliada, não é possível removê-la');
              return;
            }

            this.handleNFeError;
          },
        });
      },
      error: this.handleNFeError,
    });
  }

  onDropNfOnOrder(event: CdkDragDrop<any>, targetOrder?: PurchaseOrder) {
    if (!event || !event.container.data || !targetOrder) return;
    const draggedItem = event.item.data;
    if (!draggedItem || !targetOrder.order) return;

    const { code: nfCode, id: ndId } = draggedItem;
    const orderId = targetOrder.id;
    const { commerceReference } = targetOrder.order;

    this.confirmAction.confirm(`Você está associando a NF-e <b>${nfCode}</b> ao pedido <b>${commerceReference}</b>.`, null, true).subscribe({
      next: ({ confirmed }) => {
        if (confirmed) {
          this.associateNf(orderId, ndId);
        }
      },
      error: () => {
        this.toast.failure('Erro ao associar NF-e, tente novamente mais tarde');
      },
    });

    return;
  }

  private associateNf(orderId: string, nfCode: string) {
    this.purchaseService.associate(orderId, nfCode).subscribe({
      next: () => {
        const newPagination = { ...this.innitialPagination, page: 1 };
        this.pendingOrdersPagination.set(newPagination);
        this.invoiceSellerPagination.set(newPagination);
        this.invoiceReconciledPagination.set(newPagination);

        this.fetchInvoiceSeller(this.invoiceSellerQuery);
        this.fetchPendingOrders(this.pendingOrdersQuery);
        this.fetchInvoiceReconciledOrders(this.invoiceReconciledQuery);
        this.toast.success('NF-e associada com sucesso ao pedido');
      },
      error: (res: ErrorResponse) => {
        if (!res.error?.details) return;
        if (res.status === 409) {
          this.toast.warning('O pedido e/ou a NF-e já está(ão) associado(s) a outro(s) pedido(s).');
          return;
        } else if (res.status === 404) {
          this.toast.warning('O pedido e/ou a NF-e foi(ram) removido(s) do painel fiscal e, por isso, não é possível associá-lo(s).');
          return;
        }
        this.toast.failure('Erro ao associar NF-e, tente novamente mais tarde');
      },
    });
  }

  onLoadMorePendingOrders(pagination: Pagination) {
    this.pendingOrdersPagination.set(pagination);

    this.purchaseService.fetchOrders(this.pendingOrdersQuery).subscribe({
      next: (res) => {
        const { items } = res;
        const prevData = this.pendingOrders().data || [];
        this.pendingOrders.set({ data: [...prevData, ...items], loading: false });
        this.pendingOrdersPagination.set(this.pageableToPagination(res));
      },
      error: () => {
        this.pendingOrders.set({ failure: true });
      },
    });
  }

  onLoadMoreInvoiceSeller(pagination: Pagination) {
    this.invoiceSellerPagination.set(pagination);
    this.inboundService.fetchInboundInvoices(this.invoiceSellerQuery).subscribe({
      next: (res) => {
        const { items } = res;
        const prevData = this.invoiceSeller().data || [];
        this.invoiceSeller.set({ data: [...prevData, ...items], loading: false });
        this.invoiceSellerPagination.set(this.pageableToPagination(res));
      },
      error: () => {
        this.invoiceSeller.set({ failure: true });
      },
    });
  }

  onLoadMoreInvoiceReconciled(pagination: Pagination) {
    this.invoiceReconciledPagination.set(pagination);

    this.purchaseService.fetchOrders(this.invoiceReconciledQuery).subscribe({
      next: (res) => {
        const { items } = res;
        const prevData = this.invoiceReconciled().data || [];
        this.invoiceReconciled.set({ data: [...prevData, ...items], loading: false });
        this.invoiceReconciledPagination.set(this.pageableToPagination(res));
      },
      error: () => {
        this.invoiceReconciled.set({ failure: true });
      },
    });
  }

  onClickNfe(key: string) {
    this.inboundService.goToSefazConsultNfe(key);
  }

  previewInvoice(invoice: InboundInvoice) {
    this.purchaseService.dowloadInvoice(invoice.id).subscribe((res) => {
      this.documentPreview.open({ id: invoice.id, title: invoice.commerceReference, type: `application/pdf`, url: res });
    });
  }

  disableDragAndDrop() {
    return !this.credentials.hasRole([['compras-painel-fiscal-edicao']]);
  }
}
